import { forwardRef, useEffect, useImperativeHandle } from 'react'
import type { KeyboardEventHandler, ReactNode } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { EditorContent } from '@tiptap/react'
import { isKeyHotkey } from 'is-hotkey'
import { useTranslation } from 'next-i18next'
import { useFormContext } from 'react-hook-form'
import type { Descendant } from 'slate'
import { Range, Transforms } from 'slate'
import { Editable, ReactEditor, Slate } from 'slate-react'
import { MentionSuggestions } from 'shared/components/suggest-mention'
import type { Suggestion } from 'shared/components/suggest-mention/types'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import type { MentionInterface } from 'modules/comments/components/comment/comment.types'
import { deserializeTextHtml } from 'modules/community/hooks/slate-editor-utils'
import HoveringToolbar from 'modules/community/post/horizontal-toolbar'
import SlateHoveringToolbar from 'modules/community/post/slate-horizontal-toolbar'
import { useEditor } from '../../editor/hooks/use-editor'
import { useSlateEditor } from '../../editor/hooks/use-slate-editor'
import { insertMention } from '../../editor/utils/mention'

const initialSlateContent: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
]

export interface EditorRef {
  clear: () => void
  insertEmoji: (emoji: string) => void
}

interface AddPostContentEditorProps {
  content?: string
  mentions?: MentionInterface[]
}

const AddPostTiptapContentEditor = forwardRef<EditorRef, AddPostContentEditorProps>(
  function AddPostTiptapContentEditor(props, ref) {
    const { setValue } = useFormContext()

    const { editor, insertEmoji, clearContent } = useEditor({
      contentHtml: props.content,
      mentions: props.mentions,
      onHtmlChange: html => setValue('content', html),
    })

    useImperativeHandle(ref, () => ({
      clear: clearContent,
      insertEmoji,
    }))

    if (!editor) return null

    return (
      <div>
        <HoveringToolbar editor={editor} />
        <EditorContent editor={editor} />
      </div>
    )
  },
)

const AddPostSlateContentEditor = forwardRef<EditorRef, AddPostContentEditorProps>(
  function AddPostSlateContentEditor(props, ref) {
    const { setValue } = useFormContext()

    const {
      editor,
      content,
      serializedTextHtml,
      target,
      suggestions,
      suggestionsLoading,
      contentError,
      insertEmoji,
      setContent,
      setTarget,
      setContentError,
      updateEditorContent,
      renderElement,
      renderLeaf,
    } = useSlateEditor({
      initialContent: props.content
        ? ((deserializeTextHtml(props.content, props.mentions ?? []) as Descendant[]) ??
          initialSlateContent)
        : initialSlateContent,
    })

    useImperativeHandle(ref, () => ({
      clear: () => {
        setContent(initialSlateContent)
        setContentError('')
      },
      insertEmoji,
    }))

    useEffect(() => {
      setValue('content', serializedTextHtml)
    }, [serializedTextHtml, setValue])

    const onKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
      const { selection } = editor

      if (selection && Range.isCollapsed(selection)) {
        const { nativeEvent } = event
        if (isKeyHotkey('left', nativeEvent)) {
          event.preventDefault()
          Transforms.move(editor, { unit: 'offset', reverse: true })
          return
        }
        if (isKeyHotkey('right', nativeEvent)) {
          event.preventDefault()
          Transforms.move(editor, { unit: 'offset' })
          return
        }
      }
    }

    const handleMentionSelect = (mention: Suggestion): void => {
      if (target) {
        insertMention(
          editor,
          {
            displayName: mention.displayName,
            id: mention.userId,
            profileImageUrl: mention.profileImageUrl,
          },
          target,
        )
        setTarget(null)
        ReactEditor.focus(editor)
      }
    }

    return (
      <label className="font-medium">
        <Slate editor={editor} initialValue={content} onChange={updateEditorContent}>
          <SlateHoveringToolbar />
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={onKeyDown}
            className="max-h-[300px] min-h-[200px] overflow-x-hidden overflow-y-auto rounded-[5px] border border-text-secondary/30 bg-transparent p-[15px]"
          />
          {target && (suggestionsLoading || (suggestions && suggestions.length > 0)) && (
            <MentionSuggestions
              target={target}
              editor={editor}
              items={suggestions || []}
              isLoading={suggestionsLoading}
              onSelect={handleMentionSelect}
            />
          )}
        </Slate>
        {contentError && <p className="mt-2 text-sm text-text-error">{contentError}</p>}
      </label>
    )
  },
)

const AddPostContentEditor = forwardRef<EditorRef, AddPostContentEditorProps>(
  function AddPostContentEditor(props, ref): ReactNode {
    const { t } = useTranslation()
    const isTiptapEnabled = useFeatureIsOn(featureFlagKeys.enableCommunityTiptapPostEditor)

    return (
      <div className="mb-6">
        <p className="mb-1 text-base font-medium text-text-primary">{t('home.add_post.content')}</p>
        {isTiptapEnabled ? (
          <AddPostTiptapContentEditor ref={ref} {...props} />
        ) : (
          <AddPostSlateContentEditor ref={ref} {...props} />
        )}
      </div>
    )
  },
)

export default AddPostContentEditor
